import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { SocialLoginOverlay } from '@/components/ui/social-login-overlay';
import {
  initSentry,
  setSentryUser,
  clearSentryUser,
  tagRouteContext,
} from '@/config/sentry';
import {
  initPostHog,
  identifyPostHogUser,
  resetPostHogUser,
  capturePostHogPageview,
} from '@/config/posthog';
import SentryErrorBoundary from '@/components/common/SentryErrorBoundary';
import { initEventTracker } from '@/analytics/eventTracker';
import { LoginPromptProvider } from '@/components/auth/login/LoginPromptProvider';
import { useLoginPrompt } from '@/components/auth/login/loginPromptContext';
import { flushPendingFeedTap } from '@/utils/feed/pendingFeedTap';
import { ScrollToTop } from '@/components/common';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from 'react-router-dom';
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { LEGACY_LEGAL_PATHS } from '@/constants/legalDocuments';
import type { LegacyLegalPath } from '@/constants/legalDocuments';
import { useInviteHandler } from './hooks/useInviteHandler';
import { useRedirectHandler } from './hooks/useRedirectHandler';
import { useCountryDetection } from './hooks/useCountryDetection';
import { ResetPasswordModal } from '@/components/auth';

import { BlockedCountryProvider } from '@/hooks/useBlockedCountry';
import { TeamInviteAcceptModal } from '@/components/team';
import { RegistrationContext } from '@/types/tournament/game';
import { ProtectedRoute } from '@/components/common';
import { TeamProfileRoute } from '@/components/team-profile/TeamProfileRoute';
import { authService, persistUserToLocalStorage } from './services/authService';
import { CountryProvider } from '@/contexts/country';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import GoogleAnalytics from './analytics/GoogleAnalytics';
import GoogleTagManager from './analytics/GoogleTagManager';
import ClarityAnalytics from './analytics/ClarityAnalytics';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { retryUnlessForbidden } from '@/utils/queryRetry';
import { GetProfileResponse } from './types/auth';
import { useAuth } from '@/hooks/useAuth';
import { useChatSocketLifecycle } from '@/hooks/chat';
import { useTranslation } from 'react-i18next';
import { HelpFloatingButton } from '@/components/ui-components';
import { ConsentGate } from '@/components/consent/ConsentGate';
import { OnboardingGate } from '@/components/common/OnboardingGate';
import { TourProvider } from '@/tours';
import { RadixDirectionProvider } from '@/i18n/RadixDirectionProvider';

// Lazy load all pages for code splitting
const HomeRoute = lazy(() => import('./pages/home/HomeRoute'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const Clans = lazy(() => import('./pages/clan/Clans'));
const ClanDetail = lazy(() => import('./pages/clan/ClanDetail'));
const ClanProfile = lazy(() => import('./pages/clan/ClanProfile'));
const ClanAllTeams = lazy(() => import('./pages/clan/ClanAllTeams'));
const ClanAllAchievements = lazy(
  () => import('./pages/clan/ClanAllAchievements'),
);
const TeamDetail = lazy(() => import('./pages/clan/TeamDetail'));
const TeamProfile = lazy(() => import('./pages/clan/TeamProfile'));
const TeamAllAchievements = lazy(
  () => import('./pages/clan/TeamAllAchievements'),
);
const TeamAllTournamentHistory = lazy(
  () => import('./pages/clan/TeamAllTournamentHistory'),
);
const Organizations = lazy(() => import('./pages/organization/Organizations'));
const OrganizationDetail = lazy(
  () => import('./pages/organization/OrganizationDetail'),
);
const OrganizationProfile = lazy(
  () => import('./pages/organization/OrganizationProfile'),
);
const CreateOrganization = lazy(
  () => import('./pages/organization/CreateOrganization'),
);
const EditOrganization = lazy(
  () => import('./pages/organization/EditOrganization'),
);
const TournamentList = lazy(() => import('./pages/tournament/TournamentList'));
const TournamentDetail = lazy(
  () => import('./pages/tournament/TournamentDetail'),
);
const PublicTournamentDetail = lazy(
  () => import('./pages/tournament/PublicTournamentDetail'),
);
const Tournaments = lazy(() => import('./pages/tournament/Tournaments'));
const MyTournaments = lazy(() => import('./pages/tournament/MyTournaments'));
const Profile = lazy(() => import('./pages/user/Profile'));
const ProfileExperiencePage = lazy(
  () => import('./pages/user/ProfileExperience'),
);
const ProfileView = lazy(() => import('./pages/user/ProfileView'));

// ─── Deep-link fallback routes (mirror Android/iOS intent filters) ───────
// See `espotz-mobile-app/docs/DEEP_LINKING.md` for the full URL contract.
const InstallPage = lazy(() => import('./pages/install/Install'));
const TournamentDeepLink = lazy(() => import('./pages/app/TournamentDeepLink'));
const ClanDeepLink = lazy(() => import('./pages/app/ClanDeepLink'));
const TeamDeepLink = lazy(() => import('./pages/app/TeamDeepLink'));
const ProfileDeepLink = lazy(() => import('./pages/app/ProfileDeepLink'));
const OrgDeepLink = lazy(() => import('./pages/app/OrgDeepLink'));
const MatchDeepLink = lazy(() => import('./pages/app/MatchDeepLink'));
const PostDeepLink = lazy(() => import('./pages/app/PostDeepLink'));
const FeedRoute = lazy(() => import('./pages/feed/FeedRoute'));
const InviteDeepLink = lazy(() => import('./pages/app/InviteDeepLink'));
const AcademyCertificateDeepLink = lazy(
  () => import('./pages/app/AcademyCertificateDeepLink'),
);
const TournamentsDeepLink = lazy(
  () => import('./pages/app/TournamentsDeepLink'),
);
const AcademyDeepLink = lazy(() => import('./pages/app/AcademyDeepLink'));
const AcademyMyLearningDeepLink = lazy(
  () => import('./pages/app/AcademyMyLearningDeepLink'),
);
const OrganizationCreateDeepLink = lazy(
  () => import('./pages/app/OrganizationCreateDeepLink'),
);
const CreateTournament = lazy(
  () => import('./pages/tournament/CreateTournament'),
);
const TournamentEdit = lazy(() => import('./pages/tournament/TournamentEdit'));
const MatchShowdown = lazy(() => import('./pages/match/MatchShowdown'));
const MatchScoreEntry = lazy(() => import('./pages/match/MatchScoreEntry'));
const TournamentStructure = lazy(
  () => import('./pages/tournament/TournamentStructure'),
);
const TournamentSeeding = lazy(
  () => import('./pages/tournament/TournamentSeeding'),
);
const StageDetails = lazy(() => import('./pages/tournament/StageDetails'));
const GraphicGeneratorPage = lazy(
  () => import('./pages/tournament/GraphicGeneratorPage'),
);
const EntryFeeAndPrize = lazy(
  () => import('./pages/tournament/EntryFeeAndPrize'),
);
const TournamentRules = lazy(
  () => import('./pages/tournament/TournamentRules'),
);
const TournamentStreams = lazy(
  () => import('./pages/tournament/TournamentStreams'),
);
const TournamentSponsors = lazy(
  () => import('./pages/tournament/TournamentSponsors'),
);
const MatchesAndStakes = lazy(
  () => import('./pages/tournament/MatchesAndStakes'),
);
const Wallet = lazy(() => import('./pages/wallet/Wallet'));
const InrPayout = lazy(() => import('./pages/wallet/InrPayout'));
const TournamentDisputes = lazy(
  () => import('./pages/tournament/TournamentDisputes'),
);
const NotFound = lazy(() => import('./components/not-found/NotFound'));
const EmailVerification = lazy(() => import('./pages/user/EmailVerification'));
const EmailPreferences = lazy(() => import('./pages/user/EmailPreferences'));
const UnsubscribeConfirm = lazy(
  () => import('./pages/user/UnsubscribeConfirm'),
);
const MeldPaymentSuccess = lazy(
  () => import('./pages/wallet/MeldPaymentSuccess'),
);
const RazorpayReturn = lazy(() => import('./pages/wallet/RazorpayReturn'));
const StripePaymentSuccess = lazy(
  () => import('./pages/wallet/StripePaymentSuccess'),
);
const Feedback = lazy(() => import('./pages/support/Feedback'));
const Contact = lazy(() => import('./pages/support/Contact'));
const ComingSoon = lazy(() => import('./pages/support/ComingSoon'));
const AcademyPage = lazy(() => import('./pages/academy/Academy'));
const AcademyModuleDetailPage = lazy(
  () => import('./pages/academy/module-detail/AcademyModuleDetail'),
);
const AcademyCartPage = lazy(() => import('./pages/academy/cart/AcademyCart'));
const AcademyCertificateVerifyPage = lazy(
  () => import('./pages/academy/verify/AcademyCertificateVerify'),
);
const AcademyMyLearning = lazy(
  () => import('./pages/academy/my-learning/AcademyMyLearning'),
);
const AcademyCareerFitPage = lazy(
  () => import('./pages/academy/career-fit/AcademyCareerFit'),
);
const DeleteAccount = lazy(() => import('./pages/support/DeleteAccount'));
const DeleteAccountConfirm = lazy(
  () => import('./pages/support/DeleteAccountConfirm'),
);
const LegalDocumentPage = lazy(() => import('./pages/support/LegalDocument'));
const AmlPolicy = lazy(() => import('./pages/support/AmlPolicy'));
const ChildSafety = lazy(() => import('./pages/support/ChildSafety'));

/**
 * The built-in text for the two documents whose urls are registered with Google.
 * Rendered when the admin document cannot be loaded — these two urls must never
 * show an error page.
 */
const STATIC_LEGAL_PAGES: Record<LegacyLegalPath, React.ReactNode> = {
  'aml-policy': <AmlPolicy />,
  'child-safety': <ChildSafety />,
};
const Notifications = lazy(() => import('./pages/user/Notifications'));
const Messages = lazy(() => import('./pages/chat/Messages'));
const OnMetaPaymentSuccess = lazy(
  () => import('./pages/wallet/OnMetaPaymentSuccess'),
);
const OnMetaPaymentFailure = lazy(
  () => import('./pages/wallet/OnMetaPaymentFailure'),
);
const CompleteProfile = lazy(() => import('./pages/user/CompleteProfile'));
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: retryUnlessForbidden } },
});

// Initialize Sentry + PostHog at module load so they're ready before any
// route renders. Both no-op outside production.
initSentry();
initPostHog();
// First-party analytics (sponsor reporting). Unlike PostHog/GA this fires in EVERY environment,
// to that environment's collector — QA verifies the pipeline on dev.
initEventTracker();

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className='flex items-center justify-center min-h-screen'>
    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
  </div>
);

function AuthCallbackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t: tAuth } = useTranslation('auth');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const userId = urlParams.get('id');
    const error = urlParams.get('error');

    if (error) {
      toast.error(tAuth('login.toast.error.authenticationFailed'));
      navigate('/');
      return;
    }

    if (userId) {
      // Fetch user profile and token from backend using axios
      apiService
        .get<GetProfileResponse>(`/public/api/v1/auth/getprofile/${userId}`)
        .then(data => {
          if (data.result?.accessToken) {
            localStorage.setItem('accessToken', data.result.accessToken);
            persistUserToLocalStorage(data.result.user);
            queryClient.invalidateQueries({ queryKey: ['user'] });

            // Detect provider from query string
            const providerRaw = urlParams.get('provider') || 'Social';
            const provider =
              providerRaw.charAt(0).toUpperCase() + providerRaw.slice(1);

            toast.success(
              tAuth('login.toast.success.providerLogin', { provider }),
            );
            if (data.result?.user && !data.result.user.isProfileComplete) {
              navigate('/complete-profile', { replace: true });
            } else {
              navigate('/');
            }
          } else {
            toast.error(tAuth('login.toast.error.userDataFailed'));
            navigate('/');
          }
        })
        .catch(error => {
          console.error('Error fetching user profile:', error);
          toast.error(tAuth('login.toast.error.completionFailed'));
          navigate('/');
        });
    }
  }, [location, navigate, queryClient, tAuth]);

  return null;
}

/**
 * Backwards-compat redirect from the old `/clan/:clanIdentifier/profile*` paths to the new
 * `/clan-profile/:clanIdentifier*` layout. `<Navigate to="...">` does not interpolate route
 * params, so this small wrapper reads the param and builds the destination explicitly.
 */
function ClanProfileLegacyRedirect({
  suffix,
}: {
  suffix: '' | '/teams' | '/achievements';
}) {
  const { clanIdentifier } = useParams<{ clanIdentifier: string }>();
  if (!clanIdentifier) return <Navigate to='/clans' replace />;
  return (
    <Navigate
      to={`/clan-profile/${encodeURIComponent(clanIdentifier)}${suffix}`}
      replace
    />
  );
}

/**
 * Old `/top3-generator` route → the one generator. No `mode` is appended: the
 * card kind now follows the tournament, so the param would be ignored.
 */
function Top3StandingsLegacyRedirect() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  if (!tournamentId) return <Navigate to='/' replace />;
  return (
    <Navigate to={`/tournament/${tournamentId}/standings-generator`} replace />
  );
}

/** Old bracket GFX route → the one generator, which shows a bracket its podium. */
function GfxGeneratorLegacyRedirect() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  if (!tournamentId) return <Navigate to='/' replace />;
  return (
    <Navigate to={`/tournament/${tournamentId}/standings-generator`} replace />
  );
}

function AppContent() {
  const {
    showAuthModal,
    setShowAuthModal,
    showTeamInviteModal,
    teamInviteData,
    handleTeamInviteAccept,
    isLoadingTeamData,
    inviteError,
    membershipResult,
    handleTeamInviteModalClose,
  } = useInviteHandler();
  const { isAuthenticated, socialLoginLoading, user, refreshUser, logout } =
    useAuth();
  const { storeRedirect, handleDifferentAccount } = useRedirectHandler();
  const navigate = useNavigate();
  const location = useLocation();

  // Set Sentry + PostHog user context when authenticated. Keeping the two
  // in lockstep means a Sentry issue and a PostHog session always agree on
  // who the user was.
  useEffect(() => {
    if (isAuthenticated && user) {
      setSentryUser(user);
      identifyPostHogUser({
        id: user.id,
        email: user.email,
        username: user.username,
        countryCode: user.countryCode?.code,
      });
    } else {
      clearSentryUser();
      resetPostHogUser();
    }
  }, [isAuthenticated, user]);

  // SPA pageview tracking — React Router doesn't trigger document loads,
  // so we fire $pageview manually on every location change. PostHog's
  // built-in pageview is disabled in posthog.ts to avoid double-counting.
  useEffect(() => {
    capturePostHogPageview(
      `${location.pathname}${location.search}`,
      typeof document !== 'undefined' ? document.title : undefined,
    );
  }, [location.pathname, location.search]);

  // Tag every Sentry event with the current feature area + entity IDs from
  // the URL. Means the Sentry feed is filterable by `feature_area:tournament`
  // or `tournament_id:69fc...` without any per-component instrumentation.
  useEffect(() => {
    tagRouteContext(location.pathname);
  }, [location.pathname]);

  // Handle 401 unauthorized errors - logout and redirect to home
  useEffect(() => {
    const handleLogoutRequired = async () => {
      // Call logout function which handles all cleanup
      await logout();
      // Navigate to home page
      navigate('/', { replace: true });
    };

    window.addEventListener('auth:logout-required', handleLogoutRequired);

    return () => {
      window.removeEventListener('auth:logout-required', handleLogoutRequired);
    };
  }, [logout, navigate]);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');

  useCountryDetection({ isAuthenticated, user, refreshUser });
  useChatSocketLifecycle(isAuthenticated && !!user);

  // Invite / claim links ask for login by setting showAuthModal; they get the shared app-style
  // login dialog.
  const loginPrompt = useLoginPrompt();
  useEffect(() => {
    if (!showAuthModal) return;
    if (isAuthenticated) {
      setShowAuthModal(false);
      return;
    }
    loginPrompt.openLogin({ onDismiss: () => setShowAuthModal(false) });
  }, [isAuthenticated, loginPrompt, setShowAuthModal, showAuthModal]);

  // A feed CTA tapped while logged out is attributed once the viewer logs in (epic 21 #1970).
  useEffect(() => {
    if (isAuthenticated) void flushPendingFeedTap();
  }, [isAuthenticated]);

  // Handle email verification token redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailVerificationToken = urlParams.get('emailVerificationToken');

    if (emailVerificationToken) {
      navigate(
        `/email-verification?emailVerificationToken=${emailVerificationToken}`,
        { replace: true },
      );
    }
  }, [location, navigate]);

  // Handle reset password token redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const createNewPasswordToken = urlParams.get('createNewPasswordToken');

    if (createNewPasswordToken) {
      setResetPasswordToken(createNewPasswordToken);
      setShowResetPasswordModal(true);
      // Clean up URL by removing the token parameter
      urlParams.delete('createNewPasswordToken');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate]);

  // Handle claim token redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('claimToken');
    const redirectTo = urlParams.get('redirectTo');

    if (token) {
      // Validate token FIRST to get the email
      authService
        .validateClaimToken(token)
        .then(response => {
          const tokenEmail = response.result?.email?.toLowerCase();
          const currentUserEmail = user?.email?.toLowerCase();

          // Case 1: Valid token
          if (response.result?.valid) {
            // Case 1a: Logged in + same email → redirect directly
            if (
              isAuthenticated &&
              tokenEmail &&
              currentUserEmail &&
              tokenEmail === currentUserEmail
            ) {
              if (redirectTo) {
                navigate(redirectTo, { replace: true });
              }
              return;
            }

            // Case 1b: Logged in + different email → toast only
            if (
              isAuthenticated &&
              tokenEmail &&
              currentUserEmail &&
              tokenEmail !== currentUserEmail
            ) {
              handleDifferentAccount();
              return;
            }

            // Case 1c: Not logged in + valid token → show login
            if (!isAuthenticated) {
              if (redirectTo) {
                storeRedirect(redirectTo, tokenEmail);
              }
              setShowAuthModal(true);
            }
          }
          // Case 2: Invalid/expired token
          else {
            // Check if logged in user matches token email
            if (isAuthenticated && tokenEmail && currentUserEmail) {
              if (tokenEmail === currentUserEmail) {
                // Same user - redirect
                if (redirectTo) {
                  navigate(redirectTo, { replace: true });
                }
              } else {
                // Different user - toast only
                handleDifferentAccount();
              }
              return;
            }

            // Not logged in → show login dialog
            if (!isAuthenticated) {
              if (redirectTo) {
                storeRedirect(redirectTo, tokenEmail);
              }
              setShowAuthModal(true);
            }
          }
        })
        .catch(() => {
          // API error → show login dialog
          if (!isAuthenticated) {
            if (redirectTo) {
              storeRedirect(redirectTo);
            }
            setShowAuthModal(true);
          }
        })
        .finally(() => {
          // Clean up URL
          urlParams.delete('claimToken');
          urlParams.delete('redirectTo');
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          navigate(newUrl, { replace: true });
        });
    }
  }, [
    location,
    navigate,
    setShowAuthModal,
    isAuthenticated,
    user,
    storeRedirect,
    handleDifferentAccount,
  ]);

  // Restore redirect after authentication (with email validation)
  // Wait until profile is complete before restoring
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      // Don't restore redirect if profile still needs completion
      if (user.isProfileComplete === false) {
        return;
      }
      // `isProfileComplete` turns true on the preferences step, with 2FA still to run — replaying
      // here would yank the user out mid-wizard. The wizard replays this itself when it finishes.
      if (location.pathname === '/complete-profile') {
        return;
      }

      const pendingRedirect = sessionStorage.getItem('pendingRedirect');
      if (pendingRedirect) {
        const expectedEmail = sessionStorage.getItem('pendingRedirectEmail');

        // Validate email if expected email exists
        if (expectedEmail && expectedEmail !== user.email.toLowerCase()) {
          sessionStorage.removeItem('pendingRedirect');
          sessionStorage.removeItem('pendingRedirectEmail');
          return;
        }
        // Email matches or no validation needed, proceed with redirect
        sessionStorage.removeItem('pendingRedirect');
        sessionStorage.removeItem('pendingRedirectEmail');
        try {
          const url = new URL(pendingRedirect, window.location.origin);
          if (url.origin === window.location.origin) {
            navigate(url.pathname + url.search, { replace: true });
          }
        } catch {
          // Invalid URL, don't redirect
          console.warn('Invalid redirect URL:', pendingRedirect);
        }
      }
    }
  }, [isAuthenticated, user, navigate, location.pathname]);

  return (
    <TourProvider userId={user?.id}>
      <>
        <SocialLoginOverlay isVisible={socialLoginLoading} />
        <AuthCallbackHandler />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path='/' element={<HomeRoute />} />
            <Route path='/login' element={<LoginPage />} />
            {/* ─── Deep-link fallback routes ───────────────────────────────────
              These `/app/<type>/<id>` paths match the Android intent filter
              and iOS Universal Link contract in `espotz-mobile-app/app.config.ts`.
              Users who tap a shared link WITH the mobile app installed get
              handed off to the native app by the OS. Users WITHOUT the app
              hit these routes, which reuse the canonical page + add a
              "Open in Espotz app" banner at the top.
              ────────────────────────────────────────────────────────────── */}
            <Route path='/install' element={<InstallPage />} />
            <Route
              path='/app/tournament/:tournamentId'
              element={<TournamentDeepLink />}
            />
            <Route
              path='/app/clan/:clanIdentifier'
              element={<ClanDeepLink />}
            />
            <Route path='/app/team/:teamId' element={<TeamDeepLink />} />
            <Route
              path='/app/profile/:username'
              element={<ProfileDeepLink />}
            />
            <Route path='/app/org/:orgSlug' element={<OrgDeepLink />} />
            <Route path='/app/match/:matchId' element={<MatchDeepLink />} />
            {/* Shared feed posts (share.url). Public: works logged out. */}
            <Route path='/app/post/:postId' element={<PostDeepLink />} />
            {/* Community feed. Public: liking and commenting ask for login. */}
            <Route path='/feed' element={<FeedRoute />} />
            <Route path='/app/invite' element={<InviteDeepLink />} />
            {/* The "certificate ready" email links here. */}
            <Route
              path='/app/academy/modules/:courseKey/:moduleKey/certificate'
              element={<AcademyCertificateDeepLink />}
            />
            {/* Fixed pages our emails link to. */}
            <Route path='/app/tournaments' element={<TournamentsDeepLink />} />
            <Route path='/app/academy' element={<AcademyDeepLink />} />
            <Route
              path='/app/academy/my-learning'
              element={<AcademyMyLearningDeepLink />}
            />
            {/* Needs a login, same as `/organizations/create`. */}
            <Route
              path='/app/organizations/create'
              element={
                <ProtectedRoute>
                  <OrganizationCreateDeepLink />
                </ProtectedRoute>
              }
            />
            <Route
              path='/complete-profile'
              element={
                <ProtectedRoute>
                  <CompleteProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path='/profile/:username/game-profile'
              element={<ProfileExperiencePage />}
            />
            <Route path='/profile/:username' element={<ProfileView />} />
            <Route
              path='/clans'
              element={
                <ProtectedRoute>
                  <Clans />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clan/:clanId'
              element={
                <ProtectedRoute>
                  <ClanDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/team/:teamId'
              element={
                <ProtectedRoute>
                  <TeamDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organizations'
              element={
                <ProtectedRoute>
                  <Organizations />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clans'
              element={
                <ProtectedRoute>
                  <Clans />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clan/:clanId'
              element={
                <ProtectedRoute>
                  <ClanDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clan-profile/:clanIdentifier/teams'
              element={
                <ProtectedRoute>
                  <ClanAllTeams />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clan-profile/:clanIdentifier/achievements'
              element={
                <ProtectedRoute>
                  <ClanAllAchievements />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clan-profile/:clanIdentifier'
              element={
                <ProtectedRoute>
                  <ClanProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path='/team-profile/:teamIdentifier/achievements'
              element={
                <ProtectedRoute>
                  <TeamProfileRoute>
                    <TeamAllAchievements />
                  </TeamProfileRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path='/team-profile/:teamIdentifier/history'
              element={
                <ProtectedRoute>
                  <TeamProfileRoute>
                    <TeamAllTournamentHistory />
                  </TeamProfileRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path='/team-profile/:teamIdentifier'
              element={
                <ProtectedRoute>
                  <TeamProfileRoute>
                    <TeamProfile />
                  </TeamProfileRoute>
                </ProtectedRoute>
              }
            />
            {/* Backwards-compat redirects: old paths shipped under /clan/:id/profile* — keep them */}
            {/* linkable for shared URLs, push deeplinks, and emailed share links. */}
            <Route
              path='/clan/:clanIdentifier/profile/teams'
              element={<ClanProfileLegacyRedirect suffix='/teams' />}
            />
            <Route
              path='/clan/:clanIdentifier/profile/achievements'
              element={<ClanProfileLegacyRedirect suffix='/achievements' />}
            />
            <Route
              path='/clan/:clanIdentifier/profile'
              element={<ClanProfileLegacyRedirect suffix='' />}
            />
            <Route
              path='/team/:teamId'
              element={
                <ProtectedRoute>
                  <TeamDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organizations'
              element={
                <ProtectedRoute>
                  <Organizations />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organizations/create'
              element={
                <ProtectedRoute>
                  <CreateOrganization />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/edit'
              element={
                <ProtectedRoute>
                  <EditOrganization />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId'
              element={
                <ProtectedRoute>
                  <OrganizationDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/org/:orgSlug'
              element={
                <ProtectedRoute>
                  <OrganizationProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId'
              element={
                <ProtectedRoute>
                  <OrganizationDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/tournaments'
              element={
                <ProtectedRoute>
                  <TournamentList />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId'
              element={<PublicTournamentDetail />}
            />
            <Route
              path='/tournament/:tournamentId/admin'
              element={
                <ProtectedRoute>
                  <TournamentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/standings-generator'
              element={
                <ProtectedRoute>
                  <GraphicGeneratorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/gfx-generator'
              element={
                <ProtectedRoute>
                  <GfxGeneratorLegacyRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/top3-generator'
              element={
                <ProtectedRoute>
                  <Top3StandingsLegacyRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/edit'
              element={
                <ProtectedRoute>
                  <TournamentEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/entry-fee-prize'
              element={
                <ProtectedRoute>
                  <EntryFeeAndPrize />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/rules'
              element={
                <ProtectedRoute>
                  <TournamentRules />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/streams'
              element={
                <ProtectedRoute>
                  <TournamentStreams />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/sponsors'
              element={
                <ProtectedRoute>
                  <TournamentSponsors />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/matches'
              element={
                <ProtectedRoute>
                  <MatchesAndStakes />
                </ProtectedRoute>
              }
            />
            <Route path='/tournaments' element={<Tournaments />} />
            <Route
              path='/my-tournaments'
              element={
                <ProtectedRoute>
                  <MyTournaments />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/tournaments/create'
              element={
                <ProtectedRoute>
                  <CreateTournament />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/structure'
              element={
                <ProtectedRoute>
                  <TournamentStructure />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/seeding'
              element={
                <ProtectedRoute>
                  <TournamentSeeding />
                </ProtectedRoute>
              }
            />
            <Route
              path='/tournament/:tournamentId/structure/stage/:stageIndex'
              element={
                <ProtectedRoute>
                  <StageDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path='/profile'
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path='/profile/game-profile'
              element={
                <ProtectedRoute>
                  <ProfileExperiencePage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/messages'
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path='/messages/:conversationId'
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path='/notifications'
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path='/wallet'
              element={
                <ProtectedRoute>
                  <Wallet />
                </ProtectedRoute>
              }
            />
            <Route
              path='/wallet/inr-payout'
              element={
                <ProtectedRoute>
                  <InrPayout />
                </ProtectedRoute>
              }
            />
            <Route
              path='/wallet/meld-payment-success'
              element={<MeldPaymentSuccess />}
            />
            <Route
              path='/wallet/stripe-payment-success'
              element={
                <ProtectedRoute>
                  <StripePaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path='/wallet/razorpay-return'
              element={
                <ProtectedRoute>
                  <RazorpayReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path='/wallet/onmeta-payment-success'
              element={<OnMetaPaymentSuccess />}
            />
            <Route
              path='/wallet/onmeta-payment-failure'
              element={<OnMetaPaymentFailure />}
            />
            <Route
              path='/tournament/:tournamentId/disputes'
              element={
                <ProtectedRoute>
                  <TournamentDisputes />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/tournament/:tournamentId/match/:matchId/showdown'
              element={
                <ProtectedRoute>
                  <MatchShowdown />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/tournament/:tournamentId/match/:matchId/score-entry/:gameIndex'
              element={
                <ProtectedRoute>
                  <MatchScoreEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/tournament/:tournamentId/match/:matchId/standings-generator'
              element={
                <ProtectedRoute>
                  <GraphicGeneratorPage />
                </ProtectedRoute>
              }
            />
            <Route path='/email-verification' element={<EmailVerification />} />
            {/* Public: the unsubscribe link in an email footer. The token is the identity, so
                this never checks the session — one category, one yes/no. */}
            <Route path='/unsubscribe' element={<UnsubscribeConfirm />} />
            {/* Manage every category. Reached from the profile, so the session is the identity. */}
            <Route
              path='/email-preferences'
              element={
                <ProtectedRoute>
                  <EmailPreferences />
                </ProtectedRoute>
              }
            />
            <Route path='/feedback' element={<Feedback />} />
            <Route path='/contact' element={<Contact />} />
            <Route path='/coming-soon' element={<ComingSoon />} />
            <Route
              path='/academy/modules/:courseKey/:moduleKey'
              element={<AcademyModuleDetailPage />}
            />
            {/* Where the certificate-issued notification lands. The webapp has no certificates
                list — a certificate opens as a dialog on its module page — so this is the same
                page with that dialog already open, rather than a screen of its own. */}
            <Route
              path='/academy/modules/:courseKey/:moduleKey/certificate'
              element={<AcademyModuleDetailPage />}
            />
            <Route path='/academy/cart' element={<AcademyCartPage />} />
            <Route
              path='/academy/my-learning'
              element={<AcademyMyLearning />}
            />
            <Route
              path='/academy/career-fit'
              element={<AcademyCareerFitPage />}
            />
            <Route path='/academy' element={<AcademyPage />} />
            {/* Public certificate verification. Deliberately outside ProtectedRoute: a recruiter
                opens this from a CV or a LinkedIn post, and a credential that needs an Espotz
                account to check is worth nothing. */}
            <Route
              path='/academy/verify/:certificateCode'
              element={<AcademyCertificateVerifyPage />}
            />
            <Route
              path='/academy/verify'
              element={<AcademyCertificateVerifyPage />}
            />
            {/* Public account-deletion flow (Google Play "Delete account URL"). */}
            <Route path='/delete-account' element={<DeleteAccount />} />
            <Route
              path='/delete-account/confirm'
              element={<DeleteAccountConfirm />}
            />
            {/* Admin-published legal documents; text updates without a deploy. */}
            <Route path='/legal/:slug' element={<LegalDocumentPage />} />
            {/* Registered in the Google Play Console (AML and "Safety standards"
                URLs). They stay top-level and always render: the admin document
                when it is live, the page we shipped otherwise. */}
            {LEGACY_LEGAL_PATHS.map(slug => (
              <Route
                key={slug}
                path={`/${slug}`}
                element={
                  <LegalDocumentPage
                    slug={slug}
                    fallback={STATIC_LEGAL_PAGES[slug]}
                  />
                }
              />
            ))}
            <Route path='*' element={<NotFound />} />
          </Routes>
        </Suspense>

        <TeamInviteAcceptModal
          isOpen={showTeamInviteModal}
          onClose={handleTeamInviteModalClose}
          onAccept={handleTeamInviteAccept}
          teamData={teamInviteData}
          isLoadingTeamData={isLoadingTeamData}
          inviteError={inviteError}
          membershipResult={membershipResult}
          role={teamInviteData?.inviteParams?.role}
          context={RegistrationContext.TEAM_INVITE}
        />

        <ResetPasswordModal
          isOpen={showResetPasswordModal}
          onClose={() => {
            setShowResetPasswordModal(false);
            setResetPasswordToken('');
          }}
          token={resetPasswordToken}
        />

        {/* Global Help Floating Button */}
        <HelpFloatingButton />

        {/* Redirect-only, so it gates the public routes too. Must precede the consent gate:
            an unfinished user belongs in the wizard, which asks for terms as its first step. */}
        <OnboardingGate />

        {/* Last sibling so it paints over the app. */}
        <ConsentGate />
      </>
    </TourProvider>
  );
}

function App() {
  const googleClientId =
    '588828680748-00l9uole90evj38vfbcemiuoidf6e452.apps.googleusercontent.com';
  const gtmContainerId =
    (import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined) ||
    'GTM-K35FZ56D';

  return (
    <RadixDirectionProvider>
      <HelmetProvider>
        <SentryErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <GoogleOAuthProvider clientId={googleClientId}>
              <CountryProvider>
                <TooltipProvider>
                  <Toaster />
                  <BrowserRouter>
                    <ScrollToTop />
                    <BlockedCountryProvider>
                      <GoogleAnalytics measurementId='G-1K49GMQFT0' />
                      <GoogleTagManager containerId={gtmContainerId} />
                      <ClarityAnalytics projectId='s3k0ed6b4m' />
                      <LoginPromptProvider>
                        <AppContent />
                      </LoginPromptProvider>
                    </BlockedCountryProvider>
                  </BrowserRouter>
                </TooltipProvider>
              </CountryProvider>
            </GoogleOAuthProvider>
          </QueryClientProvider>
        </SentryErrorBoundary>
      </HelmetProvider>
    </RadixDirectionProvider>
  );
}

export default App;
